import { List } from '@refinedev/mui'
import { IconButton, ListItem, Stack, Typography, Box, alpha, Popover, RadioGroup, FormControlLabel, Radio, Paper, Tooltip, InputAdornment, TextField, Collapse, Divider } from '@mui/material'
import { FilterAlt as FilterIcon, InfoOutlined as InfoIcon, Search as SearchIcon, Close as CloseIcon, Group as GroupIcon, Person as PersonIcon, Business as BusinessIcon } from "@mui/icons-material";
import { 
    ColumnDef,
    flexRender,
} from '@tanstack/react-table'
import { useTable } from "@refinedev/react-table";
import { useMemo, useState, useRef } from 'react'
import palette from '@/palette';
import { CustomCheckbox, getNextState } from '../override/checkbox';
import { CheckboxState } from '../override/checkbox';
import { IBroadcastMemberSchema, IBroadcastFilterType } from '@/utils/zod/broadcastFormValidation';

// Helper function to categorize members
const categorizeMembers = (members: IBroadcastMemberSchema[], teamMemberIds: string[]) => {
    const customers: IBroadcastMemberSchema[] = [];
    const rooms: IBroadcastMemberSchema[] = [];
    const teamMembers: IBroadcastMemberSchema[] = [];

    members.forEach(member => {
        if ('user' in member) {
            // It's an account (customer or team member)
            if (teamMemberIds.includes(member.id)) {
                teamMembers.push(member);
            } else {
                customers.push(member);
            }
        } else {
            // It's a room
            rooms.push(member);
        }
    });

    return { customers, rooms, teamMembers };
};

export const BroadcastMemberColumn = ({
    title, 
    members, 
    onMultiStateChange,
    filterType = 'all',
    onFilterChange,
    titleIcon,
    onInfoHover,
    allowSearch = false,
    memberCounts,
    teamMemberIds = [], // Add this prop to identify team members
}: {
    title: string, 
    members: IBroadcastMemberSchema[], 
    onMultiStateChange?: (member: IBroadcastMemberSchema, currentState: CheckboxState, nextState: CheckboxState) => void,
    filterType?: IBroadcastFilterType,
    onFilterChange?: (type: IBroadcastFilterType) => void,
    titleIcon?: React.ReactNode,
    onInfoHover?: {
        text: string,
    },
    allowSearch?: boolean,
    memberCounts?: {
        all: number,
        applicable: number,
        unapplicable: number,
    },
    teamMemberIds?: string[], // Add this prop
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange?.(event.target.value as IBroadcastFilterType);
        setAnchorEl(null);
    };

    const filteredMembers = useMemo(() => {
        if (!searchQuery.trim()) return members;

        return members.filter(member => {
            const searchLower = searchQuery.toLowerCase();
            const name = (member.name || '').toLowerCase();
            const phone = ('user' in member ? String(member.user?.phone_number) : '').toLowerCase();
            
            return name.includes(searchLower) || phone.includes(searchLower);
        });
    }, [members, searchQuery]);

    // Categorize filtered members
    const { customers, rooms, teamMembers } = useMemo(() => {
        return categorizeMembers(filteredMembers, teamMemberIds);
    }, [filteredMembers, teamMemberIds]);

    const columns = useMemo<ColumnDef<IBroadcastMemberSchema>[]>(() => [
        {
            id: 'select',
            cell: ({ row }) => (
                <CustomCheckbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    onStateChange={(currentState, nextState) => onMultiStateChange?.(row.original, currentState, nextState)}
                    currentState={row.original.state}
                />
            ),
        },
        {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => (
                <Stack direction="column" sx={{ paddingLeft: 2 }}>
                    <Typography variant="body1">{(row.original.name && row.original.user?.name) ? `${row.original.name} (${row.original.user?.name})` : row.original.name ? row.original.name : row.original.user?.name ? `(${row.original.user?.name})` : ""}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {'user' in row.original ? String(row.original.user?.phone_number).replace('91', '').replace(' ', '') : ''}
                    </Typography>
                </Stack>
            ),        
        }
    ], [onMultiStateChange])

    const {
        getRowModel,
    } = useTable({
        columns,
        data: members,
        enableRowSelection: true,
        getRowId: (row) => row.id,
    })

    // Helper function to render member list
    const renderMemberList = (memberList: IBroadcastMemberSchema[], sectionTitle: string, icon: React.ReactNode) => {
        if (memberList.length === 0) return null;

        return (
            <Box key={sectionTitle}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1, px: 2, backgroundColor: 'background.default' }}>
                    {icon}
                    <Typography variant="subtitle2" color="text.secondary">
                        {sectionTitle} ({memberList.length})
                    </Typography>
                </Stack>
                <Divider />
                {memberList.map(member => {
                    const row = getRowModel().rows.find(r => r.id === member.id);
                    if (!row) return null;
                    
                    return (
                        <ListItem
                            key={row.id}
                            sx={{
                                backgroundColor: row.getIsSelected() ? alpha(palette.dark.primary.main, 0.1) : "transparent",
                                transition: "background-color 0.3s",
                                borderRadius: 1,
                                paddingLeft: 0,
                                paddingRight: 0,
                                display: "flex",
                                alignItems: "center",
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                if (onMultiStateChange) {
                                    const nextState = getNextState(row.original.state);
                                    onMultiStateChange(row.original, row.original.state, nextState);
                                } else {
                                    row.getToggleSelectedHandler();
                                }
                            }}
                        >
                            <Stack direction="row" alignItems="center" flexGrow={1}>
                                {row.getVisibleCells().map((cell) => (
                                    <Box key={cell.id} sx={{ flexGrow: cell.column.id === "name" ? 1 : 0 }}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Box>
                                ))}
                            </Stack>
                        </ListItem>
                    );
                })}
            </Box>
        );
    };

    return (
        <>
            <List 
                title={<Stack direction="row" alignItems="center" gap={2}>
                        {titleIcon}
                        <Typography variant="h5">{title}</Typography>
                    </Stack>
                } 
                wrapperProps={{
                    sx: { 
                        height: 400, 
                        overflow: 'auto', 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1 
                    }
                }}
                headerProps={{
                    sx: { 
                        position: "sticky", 
                        top: 0, 
                        backgroundColor: "background.paper", 
                        zIndex: 1000 
                    }
                }}
                contentProps={{sx: {pt: 0}}}
                headerButtons={[
                    allowSearch && (
                        <IconButton 
                            color="primary"
                            onClick={() => {
                                setShowSearch(!showSearch)
                                setTimeout(() => {
                                    searchInputRef.current?.focus();
                                }, 100);
                            }}
                        >
                            {showSearch ? <CloseIcon /> : <SearchIcon />}
                        </IconButton>
                    ),
                    onFilterChange && (
                        <IconButton 
                            color="primary"
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            aria-describedby="filter-menu"
                        >
                            <FilterIcon />
                        </IconButton>
                    ),
                    onInfoHover && (
                        <Tooltip title={onInfoHover.text} placement="top">
                            <IconButton 
                                color="primary"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    ),
                ]}
            >
                <Collapse in={showSearch}>
                    <TextField
                        fullWidth
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by name or phone"
                        inputRef={searchInputRef}
                        sx={{ 
                            mb: 2,
                            px: 2,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'background.paper',
                            }
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </Collapse>

                {/* Render sections in order: Team Members, Customers, Rooms */}
                {renderMemberList(teamMembers, "Team Members", <PersonIcon fontSize="small" />)}
                {renderMemberList(customers, "Customers", <BusinessIcon fontSize="small" />)}
                {renderMemberList(rooms, "Rooms", <GroupIcon fontSize="small" />)}
            </List>

            <Popover
                id="filter-menu"
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={()=>setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Paper sx={{ p: 2, minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter Members</Typography>
                    <RadioGroup 
                        value={filterType} 
                        onChange={handleFilterChange}
                    >
                        <FormControlLabel 
                            value="all" 
                            control={<Radio size="medium" />} 
                            label={`All${memberCounts ? ` (${memberCounts.all})` : ''}`} 
                        />
                        <FormControlLabel 
                            value="applicable" 
                            control={<Radio size="medium" />} 
                            label={`Applicable${memberCounts ? ` (${memberCounts.applicable})` : ''}`} 
                        />
                        <FormControlLabel 
                            value="unapplicable" 
                            control={<Radio size="medium" />} 
                            label={`Unapplicable${memberCounts ? ` (${memberCounts.unapplicable})` : ''}`} 
                        />
                    </RadioGroup>
                </Paper>
            </Popover>
        </>
    )
}